import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { PasswordInput } from '../../components/ui/password-input'
import { Card } from '../../components/ui/card'
import { useToast } from '../../hooks/use-toast'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { TOTPVerify } from '../../components/auth/totp-verify'
import { processAuthLink, cleanupAuthLinkUrl } from '../../lib/auth-link-utils'
import Background from '../../components/background'

export function ResetPasswordPage() {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [formError, setFormError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [hasAccessToken, setHasAccessToken] = useState(false)
  const [isProcessingAuth, setIsProcessingAuth] = useState(true)
  const [needsMfa, setNeedsMfa] = useState(false)
  const [pendingPassword, setPendingPassword] = useState('')
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()

  useEffect(() => {
    const handleAuthFlow = async () => {
      console.log('[ResetPasswordPage] useEffect - handleAuthFlow called')
      console.log('[ResetPasswordPage] Processing auth link parameters')
      setIsProcessingAuth(true)
      setIsLoading(true)

      try {
        console.log('[ResetPasswordPage] React Router location:', location)

        const actualUrl = new URL(window.location.href)
        const actualLocation = {
          search: actualUrl.search,
          hash: actualUrl.hash,
        }

        const result = await processAuthLink(supabase, actualLocation)
        console.log('[ResetPasswordPage] processAuthLink completed with success:', result.success)

        if (result.success && result.session) {
          console.log('[ResetPasswordPage] Successfully authenticated via processAuthLink')
          setHasAccessToken(true)
          cleanupAuthLinkUrl(window.location, true) // Preserve hash for routing

          // Double check that the session is actually set in Supabase
          const {
            data: { session: verifySession },
          } = await supabase.auth.getSession()
          console.log('[ResetPasswordPage] Session verification completed, has session:', !!verifySession)
        } else {
          // If processAuthLink didn't establish a session (either error or no valid link data)
          // Check for an existing valid session if already on the reset page (e.g. page refresh after token set)
          console.log('[ResetPasswordPage] processAuthLink did not establish session, checking existing session')
          const {
            data: { session: existingSession },
          } = await supabase.auth.getSession()
          console.log('[ResetPasswordPage] Existing session check completed, has session:', !!existingSession)

          const isResetPasswordPath =
            location.hash.includes('/auth/reset-password') || location.pathname.includes('/auth/reset-password')

          if (existingSession && isResetPasswordPath) {
            console.log('[ResetPasswordPage] Active existing session found on reset password page')
            setHasAccessToken(true)
          } else {
            console.log('[ResetPasswordPage] No valid link data and no active existing session on reset page.')
            setHasAccessToken(false)
            // Only show toast and navigate if there was an actual error from processAuthLink
            // or if it's clear that the link was invalid (no code, no token).
            if (
              result.error ||
              (!new URLSearchParams(actualLocation.search).get('code') && !actualLocation.hash.includes('access_token'))
            ) {
              console.log('[ResetPasswordPage] Invalid reset link, redirecting to login')
              toast({
                title: 'Error',
                description:
                  result.error?.message ||
                  'Invalid or expired password reset link. Please request a new password reset.',
                variant: 'destructive',
              })
              navigate('/auth/login', { replace: true })
            } else {
              console.log(
                '[ResetPasswordPage] Auth params present but session not established - allowing form to show but it will fail',
              )
            }
          }
        }
      } catch (error) {
        // Catch unexpected errors from processAuthLink or getSession
        console.error('[ResetPasswordPage] Unexpected error in handleAuthFlow:', error)
        setHasAccessToken(false)
        toast({
          title: 'Error',
          description: 'Failed to process password reset link. Please try again.',
          variant: 'destructive',
        })
        navigate('/auth/login', { replace: true })
      } finally {
        setIsProcessingAuth(false)
        setIsLoading(false)
      }
    }

    handleAuthFlow()
  }, [navigate, toast, location, supabase]) // Added supabase to dependencies

  const validatePassword = (password: string): string | null => {
    if (password.length < 12) {
      return 'Password must be at least 12 characters long'
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter'
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter'
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number'
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return 'Password must contain at least one special character'
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    // Validate password
    console.log('[ResetPasswordPage] handleSubmit called')

    console.log('[ResetPasswordPage] Validating password format')
    // Validate password
    const passwordError = validatePassword(password)
    if (passwordError) {
      console.log('[ResetPasswordPage] Password validation failed:', passwordError)
      setFormError(passwordError)
      return
    }

    // Check if passwords match
    if (password !== confirmPassword) {
      console.log('[ResetPasswordPage] Passwords do not match.')
      setFormError('Passwords do not match')
      return
    }

    console.log('[ResetPasswordPage] Passwords match and validation passed. Setting isLoading to true.')
    setIsLoading(true)
    try {
      // Check if we have a session before attempting password update
      console.log('[ResetPasswordPage] Checking current session before updateUser')
      const {
        data: { session: currentSession },
        error: sessionError,
      } = await supabase.auth.getSession()
      console.log('[ResetPasswordPage] Current session exists:', !!currentSession)
      console.log('[ResetPasswordPage] Session error:', sessionError)

      if (!currentSession) {
        console.error('[ResetPasswordPage] No session found when trying to update password')

        // Try to reprocess the auth link to restore the session
        console.log('[ResetPasswordPage] Attempting to restore session from auth link')
        const result = await processAuthLink(supabase, location)
        console.log('[ResetPasswordPage] Restore session completed with success:', result.success)

        if (result.success && result.session) {
          console.log('[ResetPasswordPage] Successfully restored session, proceeding with password update')
        } else {
          throw new Error('No active session found. Please try the password reset link again.')
        }
      }

      console.log('[ResetPasswordPage] Attempting supabase.auth.updateUser')
      const { error } = await supabase.auth.updateUser({ password })

      if (error) {
        console.error('[ResetPasswordPage] Error from supabase.auth.updateUser:', error)
        // Check if the error is due to MFA requirement
        if (error.message.includes('AAL2 session is required')) {
          console.log('[ResetPasswordPage] MFA is required. Setting needsMfa to true.')
          setPendingPassword(password)
          setNeedsMfa(true)
          return
        }
        throw error
      }

      console.log('[ResetPasswordPage] supabase.auth.updateUser successful.')
      // If we get here, password was updated successfully
      await handleSuccessfulPasswordReset()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password'
      console.error('[ResetPasswordPage] Catch block in handleSubmit:', errorMessage, error)
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSuccessfulPasswordReset = async () => {
    console.log('[ResetPasswordPage] handleSuccessfulPasswordReset called')
    // Since this is partner-ui, all users are partner users, so just sign out and redirect to partner login
    console.log('[ResetPasswordPage] Signing out user.')
    await supabase.auth.signOut()

    toast({
      title: 'Success',
      description: 'Your password has been reset successfully. Please log in with your new password.',
    })

    // Use replace to prevent back navigation to the reset page
    console.log('[ResetPasswordPage] Navigating to partner login page.')
    navigate('/auth/login', { replace: true })
  }

  const handleMfaComplete = async () => {
    console.log('[ResetPasswordPage] handleMfaComplete called')
    setIsLoading(true)
    try {
      console.log('[ResetPasswordPage] Attempting supabase.auth.updateUser after MFA verification')
      // Try updating password again after MFA verification
      const { error } = await supabase.auth.updateUser({ password: pendingPassword })
      if (error) {
        console.error('[ResetPasswordPage] Error from supabase.auth.updateUser in handleMfaComplete:', error)
        throw error
      }

      console.log('[ResetPasswordPage] supabase.auth.updateUser successful in handleMfaComplete.')
      // Reuse the updated handleSuccessfulPasswordReset logic
      await handleSuccessfulPasswordReset()
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password'
      console.error('[ResetPasswordPage] Catch block in handleMfaComplete:', errorMessage, error)
      setFormError(errorMessage)
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
      setNeedsMfa(false)
      setPendingPassword('')
    }
  }

  const renderContent = () => {
    if (!hasAccessToken) {
      return (
        <Alert variant="destructive">
          <AlertDescription className="text-white">
            Invalid or expired password reset link. Please request a new password reset.
          </AlertDescription>
        </Alert>
      )
    }

    if (needsMfa) {
      return (
        <div className="flex flex-col items-center justify-center space-y-4 pt-4 pb-4">
          <Alert>
            <AlertDescription>Two-factor authentication is required to complete your password reset.</AlertDescription>
          </Alert>
          <TOTPVerify onVerifySuccess={handleMfaComplete} isEmbedded={true} />
        </div>
      )
    }

    return (
      <form onSubmit={handleSubmit} className="flex flex-col w-full gap-6">
        <div className="flex flex-col gap-4 mt-2">
          <PasswordInput
            placeholder="New Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isLoading}
            className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20"
          />
          <PasswordInput
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={isLoading}
            className="bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-primary focus:ring-primary/20"
          />
        </div>
        {formError && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription className="text-white">{formError}</AlertDescription>
          </Alert>
        )}
        <div className="text-sm text-gray-200">
          Password must:
          <ul className="list-disc marker:text-color pl-5 mt-1 text-gray-200">
            <li className="text-gray-200">Be at least 12 characters long</li>
            <li className="text-gray-200">Contain at least one uppercase letter</li>
            <li className="text-gray-200">Contain at least one lowercase letter</li>
            <li className="text-gray-200">Contain at least one number</li>
            <li className="text-gray-200">Contain at least one special character</li>
          </ul>
        </div>
        <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground" disabled={isLoading}>
          {isLoading ? 'Updating...' : 'Update Password'}
        </Button>
      </form>
    )
  }

  if (isProcessingAuth) {
    return <Background />
  }

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
        <div className="flex min-h-[500px]">
          {/* Left Panel - Marketing Content */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-l-2xl">
            <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(/fanfairpartnerlogin.jpg)` }}/>
            {/* Header */}
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between z-10">
              <div className="flex items-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto"/>
              </div>
            </div>
            {/* Content */}
            <div className="relative z-10 flex items-center justify-center p-12">
              <div className="text-center text-white max-w-md">
                <h1 className="text-4xl font-bold mb-4 text-white">
                  Set New Password
                </h1>
                <p className="text-lg opacity-90 mb-8 text-white">
                  Create a new, secure password for your account
                </p>
                {/* Slide indicators */}
                <div className="flex justify-center space-x-2">
                  <div className="w-8 h-1 bg-white rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                  <div className="w-1 h-1 bg-white/40 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
          {/* Right Panel - Login Form */}
          <div className="w-full lg:w-1/2 bg-slate-800 flex items-center justify-center p-6 lg:p-12">
            <div className="w-full max-w-sm space-y-8">
              {/* Mobile Header */}
              <div className="lg:hidden text-center">
                <img src="/fanfairreversed.png" alt="FanFair" className="h-8 mx-auto"/>
              </div>
              {/*<div className="space-y-2">
                <h1 className="text-2xl font-medium text-white">Sign in to your account</h1>
                <p className="text-slate-400 font-light">
                  Welcome back! Please enter your details.
                </p>
              </div>*/}
              <div className="!mt-0">
                {renderContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
